import { useEffect, useState } from 'react';
import { api } from '../api';
import { styles, colors } from './UI';
import EventModal from './EventModal';

// Pending Event Invitations widget. Shows the caller's Pending invitations
// (events they've been invited to but haven't responded to). Each row has
// Accept / Decline buttons; clicking the title opens the full EventModal
// for context. Hides itself when there are zero pending invitations.

const TYPE_EMOJI = {
  'Luncheon': '🍽️', 'Dinner': '🍷', 'Meeting': '🤝', 'Lunch & Learn': '🎓',
  'Golf': '⛳', 'Presentation': '🎤', 'Trade Show': '🏪',
  'Network Meeting': '🌐', 'Job Walk': '👷', 'Other': '📅'
};

const fmtDateShort = (d) => {
  if (!d) return '';
  const dt = new Date(String(d).slice(0, 10) + 'T12:00:00');
  return dt.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
};

const fmtTime = (t) => {
  if (!t) return '';
  const [h, m] = String(t).split(':').map(Number);
  const hh = h === 0 ? 12 : (h > 12 ? h - 12 : h);
  const ampm = h < 12 ? 'AM' : 'PM';
  return `${hh}:${String(m).padStart(2, '0')} ${ampm}`;
};

export default function PendingInvitationsWidget({ user }) {
  const [invites, setInvites] = useState([]);
  const [error, setError] = useState('');
  const [opening, setOpening] = useState(null);
  const [busy, setBusy] = useState(null); // attendee_id being acted on

  const reload = () => {
    api.listEventInvitations().then(setInvites).catch(e => setError(e.message));
  };

  useEffect(() => { reload(); }, []);

  const respond = async (eventId, response) => {
    setBusy(eventId);
    try {
      await api.respondToEvent(eventId, response);
      reload();
    } catch (e) { setError(e.message); }
    finally { setBusy(null); }
  };

  const openEventModal = async (id) => {
    try {
      const ev = await api.getEvent(id);
      setOpening(ev);
    } catch (e) { setError(e.message); }
  };

  if (error) return <div style={styles.errorBox}>{error}</div>;
  if (!invites.length) return null;

  return (
    <div style={{ ...styles.card, borderColor: '#FBBF24', background: '#FFFBEB' }}>
      <h2 style={{ ...styles.h2, color: '#92400E', marginBottom: 12 }}>
        📩 Pending Event Invitations <span style={{ color: colors.textMuted, fontWeight: 400 }}>· {invites.length}</span>
      </h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {invites.map(inv => (
          <div key={inv.id} style={{
            background: '#fff', border: '1px solid #FCD34D', borderRadius: 6,
            padding: 12, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap'
          }}>
            <div
              onClick={() => openEventModal(inv.id)}
              style={{ flex: 1, minWidth: 240, cursor: 'pointer' }}
              title="Click for full details"
            >
              <div style={{ fontWeight: 600, fontSize: 14, color: colors.text }}>
                {TYPE_EMOJI[inv.event_type] || '📅'} {inv.title}
              </div>
              <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 2 }}>
                {fmtDateShort(inv.event_date)}
                {inv.start_time ? ` · ${fmtTime(inv.start_time)}` : ''}
                {inv.location ? ` · ${inv.location}` : ''}
                {inv.customer ? ` · ${inv.customer}` : ''}
                <span style={{ marginLeft: 6, color: '#92400E' }}>
                  from {inv.created_by_name}
                </span>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                style={{ ...styles.btnSecondary, color: colors.red, borderColor: '#FCA5A5', padding: '6px 12px', fontSize: 12 }}
                onClick={() => respond(inv.id, 'Declined')}
                disabled={busy === inv.id}
              >Decline</button>
              <button
                style={{ ...styles.btn, padding: '6px 12px', fontSize: 12 }}
                onClick={() => respond(inv.id, 'Accepted')}
                disabled={busy === inv.id}
              >{busy === inv.id ? '…' : 'Accept'}</button>
            </div>
          </div>
        ))}
      </div>
      <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 8 }}>
        Accepted events appear on your Calendar. Declined ones disappear.
      </div>

      {opening && (
        <EventModal
          event={opening}
          currentUser={user}
          onClose={() => setOpening(null)}
          onSaved={() => { setOpening(null); reload(); }}
        />
      )}
    </div>
  );
}
